import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronUp, GripVertical, ChevronRight, EyeOff, LayoutGrid } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuCheckboxItem, DropdownMenuLabel, DropdownMenuSeparator,
} from '../ui/dropdown-menu';

const Ctx = createContext(null);

const load = (key, fallback) => {
  try { const v = JSON.parse(localStorage.getItem(key)); return v ?? fallback; } catch { return fallback; }
};

// Dashboard kart düzeni: sıra + açık/kapalı durumu kullanıcının tarayıcısında (localStorage) tutulur.
export function DashboardCardBoard({ storageKey, defaultOrder, defaultCollapsed = [], children }) {
  const [order, setOrder] = useState(() => {
    const saved = load(`${storageKey}.order`, []);
    return [...saved.filter((id) => defaultOrder.includes(id)), ...defaultOrder.filter((id) => !saved.includes(id))];
  });
  const [collapsed, setCollapsed] = useState(() => load(`${storageKey}.collapsed`, defaultCollapsed));
  const [hidden, setHidden] = useState(() => load(`${storageKey}.hidden`, []));
  const [visible, setVisible] = useState([]);
  const [titles, setTitles] = useState({});
  const [dragId, setDragId] = useState(null);

  useEffect(() => { try { localStorage.setItem(`${storageKey}.order`, JSON.stringify(order)); } catch { /* ignore */ } }, [order, storageKey]);
  useEffect(() => { try { localStorage.setItem(`${storageKey}.collapsed`, JSON.stringify(collapsed)); } catch { /* ignore */ } }, [collapsed, storageKey]);
  useEffect(() => { try { localStorage.setItem(`${storageKey}.hidden`, JSON.stringify(hidden)); } catch { /* ignore */ } }, [hidden, storageKey]);

  const register = useCallback((id, title) => {
    setTitles((t) => (t[id] === title ? t : { ...t, [id]: title }));
    setVisible((v) => (v.includes(id) ? v : [...v, id]));
    return () => setVisible((v) => v.filter((x) => x !== id));
  }, []);
  const toggleHidden = useCallback((id) => setHidden((h) => (h.includes(id) ? h.filter((x) => x !== id) : [...h, id])), []);

  const move = useCallback((id, dir) => {
    setOrder((o) => {
      const vis = o.filter((x) => visible.includes(x) && !hidden.includes(x));
      const i = vis.indexOf(id);
      const target = vis[i + dir];
      if (!target) return o;
      const next = [...o];
      const a = next.indexOf(id);
      const b = next.indexOf(target);
      [next[a], next[b]] = [next[b], next[a]];
      return next;
    });
  }, [visible, hidden]);

  const dropOn = useCallback((targetId) => {
    if (!dragId || dragId === targetId) return;
    setOrder((o) => {
      const next = o.filter((x) => x !== dragId);
      next.splice(next.indexOf(targetId), 0, dragId);
      return next;
    });
    setDragId(null);
  }, [dragId]);

  const value = useMemo(() => {
    const vis = order.filter((x) => visible.includes(x) && !hidden.includes(x));
    return {
      orderOf: (id) => order.indexOf(id),
      isFirst: (id) => vis[0] === id,
      isLast: (id) => vis[vis.length - 1] === id,
      isCollapsed: (id) => collapsed.includes(id),
      isHidden: (id) => hidden.includes(id),
      toggle: (id) => setCollapsed((c) => (c.includes(id) ? c.filter((x) => x !== id) : [...c, id])),
      toggleHidden, move, register, dragId, setDragId, dropOn,
    };
  }, [order, visible, collapsed, hidden, toggleHidden, move, register, dragId, dropOn]);

  const manageable = order.filter((id) => visible.includes(id));

  return (
    <Ctx.Provider value={value}>
      <div className="flex justify-end -mb-2">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="h-7 text-xs gap-1.5" data-testid="dash-cards-manage">
              <LayoutGrid className="h-3.5 w-3.5" />Kartları Yönet
              {hidden.filter((h) => visible.includes(h)).length > 0 && (
                <span className="rounded-full bg-slate-200 dark:bg-slate-700 px-1.5 text-[10px]">{hidden.filter((h) => visible.includes(h)).length} gizli</span>
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-60">
            <DropdownMenuLabel className="text-xs">Görünecek kartlar</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {manageable.map((id) => (
              <DropdownMenuCheckboxItem
                key={id}
                checked={!hidden.includes(id)}
                onCheckedChange={() => toggleHidden(id)}
                onSelect={(e) => e.preventDefault()}
                data-testid={`dash-cards-manage-${id}`}
              >
                {titles[id] || id}
              </DropdownMenuCheckboxItem>
            ))}
            {hidden.length > 0 && (
              <>
                <DropdownMenuSeparator />
                <button type="button" onClick={() => setHidden([])} className="w-full text-left px-2 py-1.5 text-xs text-sky-600 hover:bg-slate-50 dark:hover:bg-slate-800 rounded" data-testid="dash-cards-show-all">
                  Tümünü göster
                </button>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 grid-flow-row-dense items-start gap-4" data-testid="dashboard-card-board">{children}</div>
    </Ctx.Provider>
  );
}

export function DashCard({ id, title, icon: Icon, wide = false, accent, children }) {
  const c = useContext(Ctx);
  const { register } = c;
  useEffect(() => register(id, title), [id, title, register]);
  const closed = c.isCollapsed(id);
  const [over, setOver] = useState(false);
  if (c.isHidden(id)) return null;
  return (
    <section
      style={{ order: c.orderOf(id) }}
      className={cn('min-w-0 flex flex-col gap-1.5', wide && 'lg:col-span-2', over && 'ring-2 ring-sky-400 ring-offset-2 rounded-2xl', c.dragId === id && 'opacity-50')}
      onDragOver={(e) => { if (c.dragId) { e.preventDefault(); setOver(true); } }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => { e.preventDefault(); setOver(false); c.dropOn(id); }}
      data-testid={`dash-card-${id}`}
    >
      <div className={cn('flex items-center gap-1.5 px-1 text-xs font-semibold text-slate-500 dark:text-slate-400 select-none', accent)}>
        <span
          draggable
          onDragStart={(e) => { e.dataTransfer.effectAllowed = 'move'; c.setDragId(id); }}
          onDragEnd={() => c.setDragId(null)}
          className="cursor-grab active:cursor-grabbing p-0.5 rounded hover:bg-slate-100 dark:hover:bg-slate-800"
          title="Sürükleyerek taşı"
          data-testid={`dash-card-drag-${id}`}
        >
          <GripVertical className="h-3.5 w-3.5" />
        </span>
        <button type="button" onClick={() => c.toggle(id)} className="flex items-center gap-1.5 hover:text-slate-800 dark:hover:text-slate-200" data-testid={`dash-card-toggle-${id}`}>
          {closed ? <ChevronRight className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
          {Icon && <Icon className="h-3.5 w-3.5" />}
          <span>{title}</span>
          {closed && <span className="font-normal text-[10px] text-slate-400">(kapalı)</span>}
        </button>
        <div className="ml-auto flex items-center">
          <button type="button" onClick={() => c.toggleHidden(id)} className="p-0.5 rounded hover:bg-slate-100 dark:hover:bg-slate-800" title="Kartı gizle (Kartları Yönet'ten geri açılır)" data-testid={`dash-card-hide-${id}`}>
            <EyeOff className="h-3.5 w-3.5" />
          </button>
          <button type="button" disabled={c.isFirst(id)} onClick={() => c.move(id, -1)} className="p-0.5 rounded hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-30" title="Yukarı taşı" data-testid={`dash-card-up-${id}`}>
            <ChevronUp className="h-3.5 w-3.5" />
          </button>
          <button type="button" disabled={c.isLast(id)} onClick={() => c.move(id, 1)} className="p-0.5 rounded hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-30" title="Aşağı taşı" data-testid={`dash-card-down-${id}`}>
            <ChevronDown className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
      {!closed && <div className="min-w-0 flex-1">{children}</div>}
    </section>
  );
}
